'use es6';

import PropTypes from 'prop-types';
import { useEffect, useCallback } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { isEqual } from 'underscore';
import { connect } from 'react-redux';
import { MoveableArea } from 'foundations-components/transitional/components/MoveableArea';
import FormFieldInput from 'FormsUI/components/editor/form/formFields/FormFieldInputContainer';
import FormFieldFloatingButtonGroup from 'FormsUI/components/editor/form/formFields/FormFieldFloatingButtonGroup';
import { startDrag, endDrag } from 'FormsUI/actions/DragState.ts';
import { DragTypes } from 'FormsUI/constants/DragAndDrop';
import { FieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { OPEN_PROGRESSIVE_PROFILING_FIELDS_EVENT } from 'FormsUI/constants/Events';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { isMandatoryProperty } from 'FormsUI/utils/PropertiesUtils';
import { getObjectTypeIdValue } from 'FormsCommonComponents/form-definition/utils/FormFieldUtils';
import { SELECT_FIELD, DELETE_FORM_FIELD_FLOATING_BUTTON } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import { MAKE_REQUIRED_INLINE } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import { jsx as _jsx } from "react/jsx-runtime";
const DraggableFormFieldInput = props => {
  const {
    id,
    formFieldGroupIndex,
    formFieldIndex,
    formField,
    handleInputSelect,
    handleInputDelete,
    update,
    handleShowDependentFields,
    areDependentFieldsShown,
    isRemovableField,
    selectedFieldName,
    isFormLabelPropertyEnabled,
    isQueuedField,
    formFieldError,
    isUsedInRouting,
    isDependentFieldUsedInRouting,
    removeRoutingFieldHandler,
    startDrag: startDragAction,
    endDrag: endDragAction,
    handleDrag,
    formFieldLabel,
    numberFormFieldsWithinGroup,
    formType
  } = props;
  const [{
    isDragging
  }, dragRef, dragPreviewRef] = useDrag(() => ({
    type: DragTypes.REORDER_FORM_FIELD,
    item: () => {
      setTimeout(() => {
        handleDrag(formField.name);
        startDragAction({
          type: 'formField',
          id: formField.name
        });
      });
      return {
        formFieldGroupIndex,
        formFieldIndex,
        formFieldName: formField.name,
        formFieldPropertyObjectType: formField.propertyObjectType,
        formFieldObjectTypeId: formField.objectTypeId,
        formFieldLabel,
        numberFormFieldsWithinGroup,
        isSourceQueued: isQueuedField,
        hasDependentFields: formField.dependentFieldFilters.length > 0,
        isFormLabelPropertyEnabled
      };
    },
    end: () => {
      setTimeout(() => {
        handleDrag();
        endDragAction();
      });
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  }), [formField, formFieldGroupIndex, formFieldIndex, formFieldLabel, numberFormFieldsWithinGroup, isQueuedField, isFormLabelPropertyEnabled, handleDrag]);

  // Hide default drag preview
  useEffect(() => {
    dragPreviewRef(getEmptyImage());
  }, [dragPreviewRef]);
  const isSelected = useCallback(() => {
    const formNameWithContact = `${formField.propertyObjectType}-${formField.name}`;
    const formNameWithObjectTypeId = `${formField.objectTypeId}/${formField.name}`;
    return isEqual(formNameWithContact, selectedFieldName) || isEqual(formNameWithObjectTypeId, selectedFieldName);
  }, [formField, selectedFieldName]);
  const handleSelectedChange = useCallback(({
    target: {
      value
    }
  }) => {
    handleInputSelect(value, formField);
    trackEditorInteraction(SELECT_FIELD, {
      via: 'moveable-area-click',
      isSelecting: value
    });
  }, [handleInputSelect, formField]);
  const handleEdit = useCallback(() => {
    handleInputSelect(!isSelected(), formField);
    trackEditorInteraction(SELECT_FIELD, {
      via: 'floating-button-group',
      isSelecting: !isSelected()
    });
  }, [handleInputSelect, formField, isSelected]);
  const handleEditDependentFields = useCallback(() => {
    handleInputSelect(true, formField, {
      scrollToSmartOptions: true
    });
  }, [handleInputSelect, formField]);
  const handleDelete = useCallback(() => {
    handleInputDelete(formFieldGroupIndex, formField.name, getObjectTypeIdValue(formField.objectTypeId, formField.propertyObjectType));
    handleInputSelect(false, formField);
    trackEditorInteraction(DELETE_FORM_FIELD_FLOATING_BUTTON);
    removeRoutingFieldHandler();
  }, [handleInputDelete, formFieldGroupIndex, formField, handleInputSelect, removeRoutingFieldHandler]);
  const handleShowQueuedFields = useCallback(() => {
    const event = new CustomEvent(OPEN_PROGRESSIVE_PROFILING_FIELDS_EVENT, {
      detail: {
        verification: false
      }
    });
    window.dispatchEvent(event);
  }, []);
  const hasDependentFields = formField.dependentFieldFilters.length !== 0;
  const isSmartField = formField.isSmartField;
  const isRequired = formField.required;
  const isHidden = formField.hidden;
  const topLabel = formField.fieldType !== FieldTypes.SINGLE_CHECKBOX ? formField.label : '';
  const objectTypeId = getObjectTypeIdValue(formField.objectTypeId, formField.propertyObjectType);
  return /*#__PURE__*/_jsx("div", {
    id: id,
    "data-test-id": "draggable-form-field-input",
    children: /*#__PURE__*/_jsx(FormFieldFloatingButtonGroup, {
      hasDependentFields: hasDependentFields,
      areDependentFieldsShown: areDependentFieldsShown,
      isSmartField: isSmartField,
      isQueuedField: isQueuedField,
      isRequired: isRequired,
      isHidden: isHidden,
      isMandatory: isMandatoryProperty(formType, objectTypeId, formField.name),
      isUsedInRouting: isUsedInRouting,
      isDependentFieldUsedInRouting: isDependentFieldUsedInRouting,
      isRemovable: isRemovableField,
      handleEdit: handleEdit,
      handleDelete: handleDelete,
      handleEditDependentFields: handleEditDependentFields,
      handleShowDependentFields: handleShowDependentFields,
      handleShowQueuedFields: handleShowQueuedFields,
      handleToggleRequired: () => {
        trackEditorInteraction(MAKE_REQUIRED_INLINE, {
          value: [formField.required ? 'made-not-required' : 'made-required']
        });
        update(formField.name, objectTypeId, 'required', !formField.required);
      },
      children: /*#__PURE__*/_jsx(MoveableArea, {
        connectDragSource: dragRef,
        containerDraggable: true,
        selected: isSelected(),
        onSelectedChange: handleSelectedChange,
        children: /*#__PURE__*/_jsx(FormFieldInput, {
          formField: formField,
          formFieldError: formFieldError,
          label: topLabel,
          isBeingDragged: isDragging,
          isFormLabelPropertyEnabled: isFormLabelPropertyEnabled
        })
      })
    })
  });
};
DraggableFormFieldInput.propTypes = {
  id: PropTypes.string.isRequired,
  formFieldGroupIndex: PropTypes.number.isRequired,
  formFieldIndex: PropTypes.number,
  formField: PropTypes.object.isRequired,
  handleInputSelect: PropTypes.func.isRequired,
  handleInputDelete: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
  handleShowDependentFields: PropTypes.func.isRequired,
  areDependentFieldsShown: PropTypes.bool,
  isRemovableField: PropTypes.bool,
  selectedFieldName: PropTypes.string,
  formType: PropTypes.string.isRequired,
  isFormLabelPropertyEnabled: PropTypes.bool,
  isQueuedField: PropTypes.bool,
  formFieldError: PropTypes.object,
  isUsedInRouting: PropTypes.bool.isRequired,
  isDependentFieldUsedInRouting: PropTypes.bool.isRequired,
  removeRoutingFieldHandler: PropTypes.func.isRequired,
  handleDrag: PropTypes.func.isRequired,
  formFieldLabel: PropTypes.string,
  numberFormFieldsWithinGroup: PropTypes.number,
  // Redux props
  startDrag: PropTypes.func.isRequired,
  endDrag: PropTypes.func.isRequired
};
const mapDispatchToProps = {
  startDrag,
  endDrag
};
export default connect(null, mapDispatchToProps)(DraggableFormFieldInput);